import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import type { HideoutItemId } from '../../shared/hideout';
import type { HideoutView } from '../../shared/messages/hideout';
import { emblemGlyph } from '../online-ui/gangFormat';
import { historyLine, recordLines, trophyLines } from './hideoutFormat';
import { interiorModel, type InteriorModel } from '../render/models/interiorModels';
import { loadModelPack, releaseModelPack, type ModelPack, type PackModel } from '../render/models/modelLibrary';
import { buildInteriorShell, type InteriorShell, type ShellLayout } from '../render/interiorShell';
import hideoutLayout from '../render/interiors/hideout.json';

/**
 * The gang's hideout behind 9 Pilcher Street (WP-X4, docs/HIDEOUT.md): a back parlour drawn INSTEAD of the city
 * while you're inside (render/sceneOverride.ts, as Sid's back room). Bare it is a cold room: boards, a card table
 * and three chairs, tea chests, one bare bulb. Each furnishing the gang buys appears in it — a rug, lamps and a sofa;
 * the emblem over the bar and a photo of every member; the jukebox lit up; the trophy board with the gang's honours
 * and history written on it; the pool table under its green shade; the bar with its optics and stools; bunting in
 * the gang's colours for the livery — and the members inside are there as figures with their names over them.
 *
 * Cost: the bare room is the baked shell (render/interiorShell.ts: boards, the 1970 paper over a distempered
 * dado, a Cycles lightmap and AO; four draws) plus one merged vertex-coloured mesh for its trim and furniture;
 * the furnishings another, rebuilt (on a black-free swap: it is cheap) only when what the gang owns or who is in
 * changes; four canvas textures (emblem, photos, board, names) and at most four lights. The city draws nothing
 * while this is up.
 */

/** Where the eye sits and what it looks at; the box around TARGET that has to stay on screen (m). */
const EYE = new THREE.Vector3(0.7, 2.65, 7.0);
const TARGET = new THREE.Vector3(0.0, 1.0, -1.0);
const FIT_HALF_W = 4.6;
const FIT_HALF_H = 1.7;

const W = 4.2; // half width
const BACK = -3.4;
const FRONT = 3.2;
const H = 2.9;
/** The room's box (the baked shell in art/models/blender/interior_bake.py mirrors it; interiorShell.test.ts checks). */
export const HIDEOUT_BOX = { W, BACK, FRONT, H, wall: 0.04 };
/** The shell's materials: the tiles' repeat in metres, their mean colour until loaded, a tint for the distemper. */
const SHELL_MATS = {
  boards: { tile: 'boards', size: 1.4, mean: 0x4a3622, color: 0xffffff, roughness: 0.85 },
  paperA: { tile: 'paperA', size: 1.2, mean: 0x56624d, color: 0xffffff, roughness: 0.95 },
  distemper: { tile: 'distemper', size: 0.9, mean: 0xe2ddd6, color: 0x4a3524, roughness: 0.9 },
  ceiling: { color: 0x2a241c, roughness: 0.95 },
};
const LIGHTMAP_INTENSITY = 3.0;

function hash(n: number): number {
  const s = Math.sin(n * 127.1 + 311.7) * 43758.5453;
  return s - Math.floor(s);
}

/** Merged, vertex-coloured, non-indexed boxes, cylinders and spheres: one draw call per kit. */
class Kit {
  private readonly parts: THREE.BufferGeometry[] = [];
  private n = 0;

  private push(g: THREE.BufferGeometry, color: number, vary: number): void {
    const geo = g.index ? g.toNonIndexed() : g;
    if (geo !== g) g.dispose();
    const c = new THREE.Color(color);
    const count = geo.getAttribute('position').count;
    const col = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      const k = 1 + (hash(this.n * 31 + Math.floor(i / 6)) - 0.5) * vary;
      col[i * 3] = c.r * k;
      col[i * 3 + 1] = c.g * k;
      col[i * 3 + 2] = c.b * k;
    }
    geo.setAttribute('color', new THREE.BufferAttribute(col, 3));
    if (geo.getAttribute('uv')) geo.deleteAttribute('uv');
    this.parts.push(geo);
    this.n++;
  }

  box(w: number, h: number, d: number, x: number, y: number, z: number, color: number, vary = 0.08, ry = 0): void {
    const g = new THREE.BoxGeometry(w, h, d);
    if (ry) g.rotateY(ry);
    this.push(g.translate(x, y, z), color, vary);
  }

  cyl(r: number, h: number, x: number, y: number, z: number, color: number, seg = 12, vary = 0.05, r2 = r): void {
    this.push(new THREE.CylinderGeometry(r, r2, h, seg, 1).translate(x, y, z), color, vary);
  }

  ball(r: number, x: number, y: number, z: number, color: number, seg = 10): void {
    this.push(new THREE.SphereGeometry(r, seg, Math.max(6, seg - 3)).translate(x, y, z), color, 0.02);
  }

  get empty(): boolean {
    return this.parts.length === 0;
  }

  build(): THREE.BufferGeometry {
    const g = mergeGeometries(this.parts, false);
    for (const p of this.parts) p.dispose();
    this.parts.length = 0;
    return g;
  }
}

const css = (c: number): string => `#${(c >>> 0).toString(16).padStart(6, '0').slice(-6)}`;

function canvasTexture(w: number, h: number, draw: (g: CanvasRenderingContext2D) => void): THREE.CanvasTexture | null {
  if (typeof document === 'undefined') return null;
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const g = c.getContext('2d');
  if (!g) return null;
  draw(g);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 4;
  return t;
}

// ------------------------------------------------------------------------------------------- the bare room

function buildRoom(k: Kit): void {
  // The boards, the paper over the dado and the ceiling are the baked shell; here the trim: the dado rail and the
  // skirting on the three walls, and the joists.
  for (const [w, x, z, ry] of [
    [2 * W, 0, BACK, 0],
    [FRONT - BACK, -W, (FRONT + BACK) / 2, Math.PI / 2],
    [FRONT - BACK, W, (FRONT + BACK) / 2, Math.PI / 2],
  ] as const) {
    k.box(w, 0.06, 0.12, x, 1.0, z, 0x26190f, 0.04, ry);
    k.box(w, 0.14, 0.12, x, 0.07, z, 0x22160d, 0.04, ry);
  }
  for (const z of [-2, -0.4, 1.2]) k.box(2 * W, 0.16, 0.12, 0, H - 0.08, z, 0x2e2216, 0.08);
  // The window on the left wall: frame, glazing bars (the night outside is its own glow plane).
  k.box(0.1, 1.3, 1.5, -W + 0.04, 1.75, 0.9, 0x2a1d12, 0.04);
  // Fireplace on the back wall, left of centre: surround, mantel, grate.
  k.box(1.4, 1.15, 0.22, -2.3, 0.58, BACK + 0.11, 0x5a4636, 0.08);
  k.box(1.6, 0.08, 0.32, -2.3, 1.18, BACK + 0.16, 0x2a1b10, 0.04);
  k.box(0.72, 0.62, 0.24, -2.3, 0.33, BACK + 0.13, 0x0e0b09, 0.02);
  // A card table and three chairs (the bare room's only furniture).
  k.box(1.0, 0.05, 1.0, -1.4, 0.74, 0.9, 0x5a3a22, 0.08);
  for (const [dx, dz] of [[-0.44, -0.44], [0.44, -0.44], [-0.44, 0.44], [0.44, 0.44]] as const) k.box(0.05, 0.72, 0.05, -1.4 + dx, 0.36, 0.9 + dz, 0x3a2416, 0.06);
  for (const [cx, cz, ry] of [[-1.4, 0.15, 0], [-2.15, 0.9, Math.PI / 2], [-0.65, 0.9, -Math.PI / 2]] as const) chair(k, cx, cz, ry);
  // Tea chests stacked by the front-right corner, and a crate of empties.
  k.box(0.6, 0.6, 0.6, 3.6, 0.3, 2.4, 0x6b5232, 0.12);
  k.box(0.6, 0.6, 0.6, 3.55, 0.9, 2.42, 0x735a38, 0.12, 0.2);
  k.box(0.6, 0.6, 0.6, 2.9, 0.3, 2.6, 0x654d2f, 0.12, -0.15);
  // The bulb's flex.
  k.box(0.012, 0.6, 0.012, 0.2, H - 0.3, 0.2, 0x111111, 0);
}

function chair(k: Kit, x: number, z: number, ry: number): void {
  const c = Math.cos(ry);
  const s = Math.sin(ry);
  const at = (dx: number, dz: number): [number, number] => [x + dx * c + dz * s, z - dx * s + dz * c];
  k.box(0.42, 0.05, 0.42, x, 0.46, z, 0x4a2f1c, 0.06, ry);
  for (const [dx, dz] of [[-0.18, -0.18], [0.18, -0.18], [-0.18, 0.18], [0.18, 0.18]] as const) {
    const [px, pz] = at(dx, dz);
    k.box(0.04, 0.46, 0.04, px, 0.23, pz, 0x3a2416, 0.05);
  }
  const [bx, bz] = at(0, -0.19);
  k.box(0.42, 0.5, 0.04, bx, 0.73, bz, 0x4a2f1c, 0.06, ry);
}

// ------------------------------------------------------------------------------------------- furnishings

/** Where the sofa stands (against the left wall, facing the room), the bar's counter and its stools (the Meshy pieces). */
const SOFA_AT = new THREE.Vector3(-W + 0.55, 0, -0.9);
const BAR_AT = new THREE.Vector3(1.8, 0, BACK + 1.27);
const STOOLS_X = [0.75, 1.45, 2.15, 2.85];
const STOOL_Z = BACK + 2.1;

/** The rug and the two standard lamps: kept with the Meshy sofa too. */
function sofaKit(k: Kit): void {
  // A rug in the middle of the boards: border, field, a band.
  k.box(3.2, 0.012, 2.3, -0.9, 0.006, 0.3, 0x5a1f1c, 0.04);
  k.box(2.9, 0.014, 2.0, -0.9, 0.008, 0.3, 0x7a2a22, 0.06);
  k.box(2.9, 0.016, 0.22, -0.9, 0.01, 0.3, 0xa58a4a, 0.04);
  // Two standard lamps (the shades are their own glowing mesh).
  for (const z of [-2.2, 0.4]) {
    k.cyl(0.16, 0.04, -W + 0.4, 0.02, z, 0x2a1d12);
    k.cyl(0.02, 1.4, -W + 0.4, 0.72, z, 0x8c6a3a, 8);
  }
}

function sofa(k: Kit): void {
  sofaKit(k);
  // The sofa against the left wall, facing the room.
  const x = SOFA_AT.x;
  k.box(0.9, 0.42, 2.1, x, 0.21, -0.9, 0x5b3a22, 0.06);
  k.box(0.22, 0.85, 2.1, x - 0.35, 0.55, -0.9, 0x5b3a22, 0.06);
  for (const dz of [-1.0, 1.0]) k.box(0.9, 0.62, 0.2, x, 0.38, -0.9 + dz * 1.0, 0x4f3220, 0.06);
  for (const dz of [-0.48, 0.48]) k.box(0.62, 0.14, 0.94, x + 0.08, 0.49, -0.9 + dz, 0x6b4428, 0.08);
}

/** The back shelves and their bottles behind the Meshy counter (it brings its own optics, foot rail and stools). */
function barKit(k: Kit): void {
  const mid = BAR_AT.x;
  const len = 3.0;
  for (const y of [1.25, 1.6]) k.box(len - 0.2, 0.04, 0.24, mid, y, BACK + 0.16, 0x3a1a0e, 0.04);
  const bottle = [0x2f5a2a, 0x7a4a1a, 0xc9b98a, 0x6a1a1a, 0x1f3a5a, 0x8a6a2a];
  for (let i = 0; i < 14; i++) {
    const bx = mid - len / 2 + 0.3 + i * ((len - 0.6) / 13);
    const y = i % 2 ? 1.6 : 1.25;
    k.cyl(0.04, 0.24, bx, y + 0.14, BACK + 0.16, bottle[i % bottle.length]!, 8, 0.1);
    k.cyl(0.015, 0.07, bx, y + 0.3, BACK + 0.16, 0x222222, 6);
  }
}

function bar(k: Kit): void {
  const z = BACK + 0.95;
  const x0 = 0.6;
  const x1 = 3.9;
  const mid = (x0 + x1) / 2;
  const len = x1 - x0;
  // The counter: panelled mahogany front, a thick top, a brass foot rail.
  k.box(len, 1.0, 0.55, mid, 0.5, z, 0x4a1f12, 0.06);
  for (let i = 0; i < 7; i++) k.box(0.03, 0.8, 0.02, x0 + 0.25 + i * ((len - 0.5) / 6), 0.5, z + 0.285, 0x2e130b, 0.04);
  k.box(len + 0.1, 0.07, 0.68, mid, 1.035, z, 0x5a2616, 0.04);
  k.cyl(0.025, len, mid, 0.18, z + 0.42, 0xc9a24a, 8);
  // The back shelves, the optics and their bottles.
  for (const y of [1.25, 1.6]) k.box(len - 0.2, 0.04, 0.24, mid, y, BACK + 0.16, 0x3a1a0e, 0.04);
  const bottle = [0x2f5a2a, 0x7a4a1a, 0xc9b98a, 0x6a1a1a, 0x1f3a5a, 0x8a6a2a];
  for (let i = 0; i < 14; i++) {
    const bx = x0 + 0.3 + i * ((len - 0.6) / 13);
    const y = i % 2 ? 1.6 : 1.25;
    k.cyl(0.04, 0.24, bx, y + 0.14, BACK + 0.16, bottle[i % bottle.length]!, 8, 0.1);
    k.cyl(0.015, 0.07, bx, y + 0.3, BACK + 0.16, 0x222222, 6);
  }
  // Optics hung upside down on the top shelf.
  for (let i = 0; i < 5; i++) {
    const bx = mid - 1 + i * 0.5;
    k.cyl(0.05, 0.2, bx, 2.05, BACK + 0.12, [0x7a4a1a, 0x9a8a5a, 0x5a1a12, 0x8a6a2a, 0x2f4a2a][i]!, 8);
    k.cyl(0.02, 0.08, bx, 1.91, BACK + 0.12, 0xc0c0c0, 6);
  }
  // Four stools.
  for (let i = 0; i < 4; i++) {
    const sx = x0 + 0.5 + i * 0.85;
    k.cyl(0.19, 0.06, sx, 0.74, z + 0.72, 0x5a2616, 14);
    k.cyl(0.03, 0.72, sx, 0.36, z + 0.72, 0x2a2a2a, 8);
    k.cyl(0.14, 0.02, sx, 0.25, z + 0.72, 0x9a7a3a, 12);
  }
}

/** Where the pool table and the jukebox stand (the Meshy pieces stand on the same spots). */
const POOL_AT = new THREE.Vector3(1.25, 0, 0.75);
const JUKEBOX_AT = new THREE.Vector3(W - 0.45, 0, -1.55);

/** The cues in the rack on the right wall and the lamp's chain: kept with the Meshy table too. */
function poolKit(k: Kit): void {
  const { x, z } = POOL_AT;
  k.box(0.02, 1.5, 0.02, W - 0.12, 0.9, 1.6, 0xb08a52, 0.04);
  k.box(0.02, 1.5, 0.02, W - 0.12, 0.9, 1.75, 0xb08a52, 0.04);
  k.box(0.01, 0.42, 0.01, x, H - 0.21, z, 0x222222, 0);
}

function pool(k: Kit): void {
  const { x, z } = POOL_AT;
  // Frame, cushions, legs, pockets; the felt is its own material.
  k.box(2.5, 0.16, 1.45, x, 0.74, z, 0x4a2414, 0.05);
  for (const [dx, dz, w, d] of [[0, -0.66, 2.4, 0.1], [0, 0.66, 2.4, 0.1], [-1.19, 0, 0.1, 1.3], [1.19, 0, 0.1, 1.3]] as const) k.box(w, 0.06, d, x + dx, 0.85, z + dz, 0x5a2c18, 0.05);
  for (const [dx, dz] of [[-1.1, -0.58], [1.1, -0.58], [-1.1, 0.58], [1.1, 0.58]] as const) k.box(0.14, 0.66, 0.14, x + dx, 0.33, z + dz, 0x3a1a0e, 0.05);
  for (const [dx, dz] of [[-1.17, -0.64], [0, -0.66], [1.17, -0.64], [-1.17, 0.64], [0, 0.66], [1.17, 0.64]] as const) k.cyl(0.055, 0.02, x + dx, 0.885, z + dz, 0x0a0a0a, 10);
  // The balls: a rack of reds and yellows, the black, the white off to one side.
  const y = 0.83 + 0.027;
  let i = 0;
  for (let row = 0; row < 4; row++) {
    for (let j = 0; j <= row; j++) {
      const bx = x + 0.45 + row * 0.048;
      const bz = z + (j - row / 2) * 0.056;
      k.ball(0.027, bx, y, bz, row === 2 && j === 1 ? 0x101010 : i++ % 2 ? 0xc0201a : 0xe0b020);
    }
  }
  k.ball(0.027, x - 0.6, y, z + 0.1, 0xf0ece0);
  // A cue on the table; the rack on the right wall and the shade's chain.
  k.box(1.45, 0.016, 0.016, x - 0.2, 0.87, z - 0.3, 0xb08a52, 0.04, 0.15);
  poolKit(k);
}

function jukebox(k: Kit): void {
  const { x, z } = JUKEBOX_AT;
  k.box(0.5, 1.1, 0.95, x, 0.55, z, 0x3a2016, 0.05);
  k.box(0.05, 0.5, 0.75, x - 0.26, 0.45, z, 0x8a6a3a, 0.06); // the grille
  k.box(0.05, 0.3, 0.75, x - 0.26, 0.92, z, 0x1a1210, 0.02); // the record window
  k.cyl(0.03, 0.95, x - 0.2, 0.06, z, 0xb08a52, 8);
}

function trophies(k: Kit): void {
  // A shelf under the board on the back wall, with cups on it.
  const z = BACK + 0.14;
  k.box(1.6, 0.04, 0.24, -0.5, 0.74, z, 0x3a1a0e, 0.04);
  for (let i = 0; i < 4; i++) {
    const x = -1.05 + i * 0.36;
    k.cyl(0.05, 0.03, x, 0.775, z, 0x2a1a0e, 10);
    k.cyl(0.07, 0.14, x, 0.86, z, 0xd4af37, 12, 0.08, 0.04);
    k.cyl(0.015, 0.04, x, 0.97, z, 0xd4af37, 6);
  }
}

function bunting(k: Kit, a: number, b: number): void {
  // Pennants in the gang's two colours along the back wall and down the right wall.
  const tri = (x: number, z: number, ry: number, c: number): void => k.box(0.22, 0.24, 0.012, x, H - 0.33, z, c, 0.06, ry);
  for (let i = 0; i < 16; i++) tri(-W + 0.4 + i * 0.5, BACK + 0.06, 0, i % 2 ? b : a);
  for (let i = 0; i < 11; i++) tri(W - 0.06, BACK + 0.4 + i * 0.5, Math.PI / 2, i % 2 ? b : a);
  k.box(2 * W - 0.4, 0.012, 0.012, 0, H - 0.21, BACK + 0.06, 0xd8d0b8, 0);
}

/** A member in the room: legs, a coat in the gang's colour (darkened), a head. Seated when `sit`. */
function figure(k: Kit, x: number, z: number, ry: number, coat: number, sit: boolean, i: number): number {
  const skin = [0xc89a78, 0x8a5a3a, 0xe0b090, 0x5a3a26][i % 4]!;
  const trouser = [0x2a2a30, 0x3a2e22, 0x1e2430][i % 3]!;
  const hip = sit ? 0.5 : 0.9;
  if (sit) {
    const c = Math.cos(ry);
    const s = Math.sin(ry);
    k.box(0.3, 0.12, 0.42, x + 0.21 * s, hip, z + 0.21 * c, trouser, 0.05, ry);
    k.box(0.26, 0.48, 0.12, x + 0.4 * s, hip - 0.25, z + 0.4 * c, trouser, 0.05, ry);
  } else {
    k.box(0.13, hip, 0.14, x - 0.08, hip / 2, z, trouser, 0.05, ry);
    k.box(0.13, hip, 0.14, x + 0.08, hip / 2, z, trouser, 0.05, ry);
  }
  k.box(0.42, 0.6, 0.26, x, hip + 0.32, z, coat, 0.06, ry);
  k.cyl(0.07, 0.08, x, hip + 0.66, z, skin, 8);
  k.ball(0.12, x, hip + 0.8, z, skin, 12);
  return hip + 1.08;
}

/** Spots for members inside, best first: [x, z, facing (ry), seated]. */
const SPOTS: readonly [number, number, number, boolean][] = [
  [-1.4, 0.15, 0, true],
  [-2.15, 0.9, Math.PI / 2, true],
  [-0.65, 0.9, -Math.PI / 2, true],
  [0.2, 1.9, Math.PI * 0.85, false],
  [2.0, 1.85, Math.PI, false],
  [-0.4, -1.6, 0.4, false],
  [2.6, -0.4, -0.6, false],
  [-3.0, 2.2, Math.PI * 0.75, false],
];
/** With the sofa: two seats on it; with the bar: stools. Used before the plain spots. */
const SOFA_SPOTS: readonly [number, number, number, boolean][] = [
  [-W + 0.62, -1.35, Math.PI / 2, true],
  [-W + 0.62, -0.45, Math.PI / 2, true],
];
const BAR_SPOTS: readonly [number, number, number, boolean][] = [
  [1.1, BACK + 1.67, Math.PI, true],
  [1.95, BACK + 1.67, Math.PI, true],
];
/** The same two seats on the Meshy stools (they stand further out: the counter is deeper). */
const MESHY_BAR_SPOTS: readonly [number, number, number, boolean][] = [
  [STOOLS_X[1]!, STOOL_Z, Math.PI, true],
  [STOOLS_X[2]!, STOOL_Z, Math.PI, true],
];

function darken(c: number, k: number): number {
  const col = new THREE.Color(c);
  col.multiplyScalar(k);
  return col.getHex();
}

// ------------------------------------------------------------------------------------------- textures

function emblemTexture(v: HideoutView): THREE.CanvasTexture | null {
  return canvasTexture(256, 256, (g) => {
    g.fillStyle = '#20160f';
    g.fillRect(0, 0, 256, 256);
    g.beginPath();
    g.arc(128, 120, 104, 0, Math.PI * 2);
    g.fillStyle = css(v.color);
    g.fill();
    g.lineWidth = 10;
    g.strokeStyle = css(v.color2);
    g.stroke();
    g.fillStyle = css(v.color2);
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.font = "120px 'Segoe UI Symbol', 'Apple Symbols', sans-serif";
    g.fillText(emblemGlyph(v.emblem), 128, 112);
    g.fillStyle = '#efe6cf';
    g.font = "bold 40px 'BH Anton', Impact, 'Arial Narrow', sans-serif";
    g.fillText(v.tag, 128, 236);
  });
}

function photosTexture(v: HideoutView): THREE.CanvasTexture | null {
  const names = v.members.slice(0, 12);
  const cols = Math.min(6, Math.max(1, names.length));
  const rows = Math.ceil(names.length / cols) || 1;
  return canvasTexture(512, 256, (g) => {
    g.clearRect(0, 0, 512, 256);
    const cw = 512 / 6;
    const ch = rows > 1 ? 128 : 200;
    names.forEach((name, i) => {
      const cx = (i % cols) * cw + (512 - cols * cw) / 2;
      const cy = Math.floor(i / cols) * 128 + (rows > 1 ? 0 : 28);
      // The frame, the mount, a sepia print of a head and shoulders, the name on a slip.
      g.fillStyle = '#2a1a0e';
      g.fillRect(cx + 6, cy + 4, cw - 12, ch - 10);
      g.fillStyle = '#d8ccb0';
      g.fillRect(cx + 12, cy + 10, cw - 24, ch - 40);
      g.fillStyle = `hsl(32, 28%, ${42 + hash(i + name.length) * 18}%)`;
      g.fillRect(cx + 16, cy + 14, cw - 32, ch - 50);
      g.fillStyle = '#3a2a1c';
      const mx = cx + cw / 2;
      const my = cy + 14 + (ch - 50) * 0.42;
      g.beginPath();
      g.arc(mx, my, (ch - 50) * 0.18, 0, Math.PI * 2);
      g.fill();
      g.fillRect(mx - (cw - 32) * 0.32, my + (ch - 50) * 0.2, (cw - 32) * 0.64, (ch - 50) * 0.4);
      g.fillStyle = '#efe6cf';
      g.textAlign = 'center';
      g.font = "15px 'BH Elite', 'Courier New', monospace";
      g.fillText(name.slice(0, 10), mx, cy + ch - 18);
    });
  });
}

function boardTexture(v: HideoutView, now: number): THREE.CanvasTexture | null {
  return canvasTexture(512, 640, (g) => {
    g.fillStyle = '#1f2a20';
    g.fillRect(0, 0, 512, 640);
    g.strokeStyle = '#6b4a28';
    g.lineWidth = 18;
    g.strokeRect(9, 9, 494, 622);
    g.fillStyle = '#d4af37';
    g.textAlign = 'center';
    g.font = "bold 42px 'BH Anton', Impact, 'Arial Narrow', sans-serif";
    g.fillText(v.name.toUpperCase().slice(0, 18), 256, 66);
    g.fillStyle = '#efe6cf';
    g.font = "17px 'BH Elite', 'Courier New', monospace";
    g.textAlign = 'left';
    let y = 104;
    const line = (t: string, color = '#efe6cf', size = 17): void => {
      if (y > 612) return;
      g.fillStyle = color;
      g.font = `${size}px 'BH Elite', 'Courier New', monospace`;
      // Wrap at about 44 characters.
      const words = t.split(' ');
      let cur = '';
      for (const w of words) {
        if ((cur + ' ' + w).trim().length > 44) {
          g.fillText(cur, 32, y);
          y += size + 6;
          cur = w;
        } else cur = (cur + ' ' + w).trim();
      }
      if (cur) {
        g.fillText(cur, 32, y);
        y += size + 6;
      }
    };
    for (const l of recordLines(v, now)) line(l);
    y += 8;
    const honours = trophyLines(v);
    line(honours.length ? 'HONOURS' : 'HONOURS — none yet', '#d4af37', 19);
    for (const h of honours.slice(0, 9)) line(`★ ${h}`);
    y += 8;
    const hist = v.history.map(historyLine);
    if (hist.length) {
      line('HISTORY', '#d4af37', 19);
      for (const h of hist.slice(0, 8)) line(`· ${h}`);
    }
  });
}

function nameTexture(name: string, you: boolean): THREE.CanvasTexture | null {
  return canvasTexture(256, 64, (g) => {
    g.clearRect(0, 0, 256, 64);
    g.font = "bold 30px 'BH Anton', Impact, 'Arial Narrow', sans-serif";
    const w = Math.min(248, g.measureText(name).width + 28);
    g.fillStyle = you ? 'rgba(212,175,55,0.92)' : 'rgba(14,11,9,0.78)';
    g.fillRect(128 - w / 2, 10, w, 44);
    g.fillStyle = you ? '#1a120a' : '#efe6cf';
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.fillText(name, 128, 33);
  });
}

export interface Stage {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** The furnishings and figures: rebuilt when the gang's view changes. */
interface Dressing {
  group: THREE.Group;
  disposables: { dispose(): void }[];
  jukeLight: THREE.PointLight | null;
  jukeMat: THREE.MeshStandardMaterial | null;
  /** The Meshy jukebox's lit panels (when it stands in for the procedural one). */
  jukeGlow: ((level: number) => void) | null;
}

export class HideoutScene {
  readonly scene = new THREE.Scene();
  readonly camera = new THREE.PerspectiveCamera(45, 1, 0.05, 40);
  private readonly disposables: { dispose(): void }[] = [];
  private readonly bulb: THREE.PointLight;
  /** The baked floor, walls and ceiling (tests, the debug state: `shell.loaded`). */
  readonly shell: InteriorShell;
  private dressing: Dressing | null = null;
  private dressKey = '';
  private time = 0;
  /** What is on show (for tests and the debug hook). */
  shown: HideoutItemId[] = [];
  figures = 0;

  /** The Meshy furniture (render/models, `hideout` pack): MEDIUM and HIGH; LOW keeps the procedural pieces. */
  private pack: ModelPack | null = null;
  private disposed = false;
  private last: { v: HideoutView; now: number } | null = null;

  constructor(
    private readonly shadows: boolean,
    detailed = shadows,
  ) {
    if (detailed) {
      void loadModelPack('hideout').then((pack) => {
        if (this.disposed || !pack) return;
        this.pack = pack;
        // dress again with the Meshy pieces (it loads in the time the panel takes to open)
        this.dressKey = '';
        if (this.last) this.setGang(this.last.v, this.last.now);
      });
    }
    const sc = this.scene;
    sc.name = 'hideout';
    sc.background = new THREE.Color(0x0b0907);
    sc.fog = new THREE.Fog(0x0b0907, 7, 16);

    // The shell: boards, paper, dado and ceiling with the baked bounce and occlusion; then the trim and furniture.
    this.shell = buildInteriorShell(hideoutLayout as ShellLayout, { specs: SHELL_MATS, lightMapIntensity: LIGHTMAP_INTENSITY, load: typeof document !== 'undefined' });
    sc.add(this.shell.mesh);
    this.disposables.push(this.shell);
    const kit = new Kit();
    buildRoom(kit);
    const geo = kit.build();
    const mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.9 });
    const room = new THREE.Mesh(geo, mat);
    room.receiveShadow = true;
    sc.add(room);
    this.disposables.push(geo, mat);

    // The night outside the window: a cool glow behind net curtains.
    const winGeo = new THREE.PlaneGeometry(1.4, 1.2);
    const winMat = new THREE.MeshBasicMaterial({ color: 0x121a28, fog: false });
    const win = new THREE.Mesh(winGeo, winMat);
    win.rotation.y = Math.PI / 2;
    win.position.set(-W + 0.1, 1.75, 0.9);
    sc.add(win);
    const netMat = new THREE.MeshBasicMaterial({ color: 0x8a8478, transparent: true, opacity: 0.12, depthWrite: false });
    const net = new THREE.Mesh(winGeo, netMat);
    net.rotation.y = Math.PI / 2;
    net.position.set(-W + 0.14, 1.75, 0.9);
    sc.add(net);
    this.disposables.push(winGeo, winMat, netMat);

    // The bare bulb in the middle of the ceiling: always there.
    const bulbGeo = new THREE.SphereGeometry(0.05, 12, 8);
    const bulbMat = new THREE.MeshStandardMaterial({ color: 0x000000, emissive: 0xffd9a0, emissiveIntensity: 5 });
    const bulbMesh = new THREE.Mesh(bulbGeo, bulbMat);
    bulbMesh.position.set(0.2, H - 0.62, 0.2);
    sc.add(bulbMesh);
    this.disposables.push(bulbGeo, bulbMat);
    this.bulb = new THREE.PointLight(0xffc488, 9, 0, 1.6);
    this.bulb.position.set(0.2, H - 0.7, 0.2);
    if (shadows) {
      this.bulb.castShadow = true;
      this.bulb.shadow.mapSize.set(512, 512);
      this.bulb.shadow.bias = -0.002;
    }
    sc.add(this.bulb);
    const hemi = new THREE.HemisphereLight(0x8a7a62, 0x1a120c, 0.55);
    sc.add(hemi);
    this.disposables.push(this.bulb, hemi);

    this.camera.position.copy(EYE);
    this.camera.lookAt(TARGET);
  }

  /** Dress the room for the gang's view (only rebuilt when something you'd see changed). */
  setGang(v: HideoutView, now: number): void {
    this.last = { v, now };
    const owned = new Set(v.items.map((i) => i.id));
    const key = JSON.stringify([[...owned].sort(), v.inside.map((i) => i.cid), v.members, v.trophies.length, v.history.length, v.color, v.color2, v.emblem, v.tag, v.name, v.freightRuns]);
    if (key === this.dressKey) return;
    this.dressKey = key;
    this.undress();
    const group = new THREE.Group();
    group.name = 'hideout-dressing';
    const disposables: { dispose(): void }[] = [];
    const kit = new Kit();
    const has = (id: HideoutItemId): boolean => owned.has(id);
    const meshyPool = has('pool') ? (this.pack?.get('pool-table') ?? null) : null;
    const meshyJuke = has('jukebox') ? (this.pack?.get('jukebox') ?? null) : null;
    const meshySofa = has('sofa') ? (this.pack?.get('sofa') ?? null) : null;
    // the bar is one furnishing: the counter and the stools come as a pair or not at all
    const meshyStool = has('bar') ? (this.pack?.get('bar-stool') ?? null) : null;
    const meshyBar = meshyStool ? (this.pack?.get('bar-counter') ?? null) : null;
    let jukeGlow: ((level: number) => void) | null = null;
    const place = (model: PackModel, at: THREE.Vector3, ry: number): InteriorModel => {
      const m = interiorModel(model);
      m.mesh.position.copy(at);
      m.mesh.rotation.y = ry;
      m.mesh.castShadow = this.shadows;
      m.mesh.receiveShadow = true;
      group.add(m.mesh);
      disposables.push(m.material);
      return m;
    };
    if (meshySofa) {
      place(meshySofa, SOFA_AT, Math.PI / 2);
      sofaKit(kit);
    } else if (has('sofa')) sofa(kit);
    if (meshyBar && meshyStool) {
      place(meshyBar, BAR_AT, 0);
      // the four stools: one instanced draw, each turned its own way
      const stool = interiorModel(meshyStool);
      const stools = new THREE.InstancedMesh(meshyStool.geometry, stool.material, STOOLS_X.length);
      stools.name = stool.mesh.name;
      const m4 = new THREE.Matrix4();
      const q = new THREE.Quaternion();
      STOOLS_X.forEach((x, i) => stools.setMatrixAt(i, m4.compose(new THREE.Vector3(x, 0, STOOL_Z), q.setFromAxisAngle(new THREE.Vector3(0, 1, 0), 0.3 + i * 1.1), new THREE.Vector3(1, 1, 1))));
      stools.computeBoundingSphere();
      stools.castShadow = this.shadows;
      stools.receiveShadow = true;
      group.add(stools);
      disposables.push(stool.material, stools);
      barKit(kit);
    } else if (has('bar')) bar(kit);
    if (meshyPool) {
      place(meshyPool, POOL_AT, 0);
      poolKit(kit);
    } else if (has('pool')) pool(kit);
    if (meshyJuke) jukeGlow = place(meshyJuke, JUKEBOX_AT, -Math.PI / 2).setGlow;
    else if (has('jukebox')) jukebox(kit);
    if (has('trophies')) trophies(kit);
    if (has('livery')) bunting(kit, v.color, v.color2);

    // Who is in: seats first (the sofa, the stools), then the table and the floor.
    const spots = [...(has('sofa') ? SOFA_SPOTS : []), ...(has('bar') ? (meshyBar ? MESHY_BAR_SPOTS : BAR_SPOTS) : []), ...SPOTS];
    const coat = darken(v.color, 0.55);
    const labels: { name: string; you: boolean; pos: THREE.Vector3 }[] = [];
    v.inside.slice(0, spots.length).forEach((m, i) => {
      const [x, z, ry, sit] = spots[i]!;
      const top = figure(kit, x, z, ry, coat, sit, m.cid);
      labels.push({ name: m.cid === v.you.cid ? 'You' : m.name, you: m.cid === v.you.cid, pos: new THREE.Vector3(x, top + 0.18, z) });
    });
    this.figures = labels.length;

    if (!kit.empty) {
      const geo = kit.build();
      const mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.75 });
      const mesh = new THREE.Mesh(geo, mat);
      mesh.castShadow = this.shadows;
      mesh.receiveShadow = true;
      group.add(mesh);
      disposables.push(geo, mat);
    }

    const plane = (w: number, h: number, tex: THREE.Texture | null, pos: [number, number, number], ry: number, opts: { transparent?: boolean; emissive?: number } = {}): void => {
      if (!tex) return;
      const g = new THREE.PlaneGeometry(w, h);
      const m = new THREE.MeshStandardMaterial({ map: tex, roughness: 0.85, transparent: !!opts.transparent, ...(opts.emissive ? { emissive: 0xffffff, emissiveMap: tex, emissiveIntensity: opts.emissive } : {}) });
      const p = new THREE.Mesh(g, m);
      p.position.set(...pos);
      p.rotation.y = ry;
      group.add(p);
      disposables.push(g, m, tex);
    };

    if (has('photos')) {
      plane(0.9, 0.9, emblemTexture(v), [-2.3, 1.78, BACK + 0.06], 0, { emissive: 0.08 });
      plane(2.0, 1.0, photosTexture(v), [-W + 0.06, 1.85, -2.2], Math.PI / 2, { transparent: true });
    }
    if (has('trophies')) plane(1.4, 1.75, boardTexture(v, now), [-0.5, 1.72, BACK + 0.06], 0, { emissive: 0.4 });

    // Glowing parts: lamp shades, the pool lamp, the jukebox's arch.
    let jukeLight: THREE.PointLight | null = null;
    let jukeMat: THREE.MeshStandardMaterial | null = null;
    const glow = (geo: THREE.BufferGeometry, color: number, intensity: number, pos: [number, number, number]): THREE.MeshStandardMaterial => {
      const m = new THREE.MeshStandardMaterial({ color: 0x000000, emissive: color, emissiveIntensity: intensity, roughness: 0.6 });
      const mesh = new THREE.Mesh(geo, m);
      mesh.position.set(...pos);
      group.add(mesh);
      disposables.push(geo, m);
      return m;
    };
    if (has('sofa')) {
      for (const z of [-2.2, 0.4]) glow(new THREE.CylinderGeometry(0.16, 0.26, 0.3, 14, 1, true), 0xffb860, 1.6, [-W + 0.4, 1.5, z]);
      const lamp = new THREE.PointLight(0xffb060, 4, 6, 1.6);
      lamp.position.set(-W + 0.5, 1.45, -0.9);
      group.add(lamp);
      disposables.push(lamp);
    }
    if (has('pool')) {
      glow(new THREE.CylinderGeometry(0.16, 0.42, 0.22, 18, 1, true), 0x2f6a3a, 1.2, [1.25, H - 0.55, 0.75]);
      const spot = new THREE.SpotLight(0xfff0d0, 14, 0, 0.75, 0.6, 1.6);
      spot.position.set(1.25, H - 0.65, 0.75);
      spot.target.position.set(1.25, 0, 0.75);
      group.add(spot, spot.target);
      disposables.push(spot);
    }
    if (has('pool') && !meshyPool) {
      // The felt: soft, its own material.
      const fg = new THREE.BoxGeometry(2.28, 0.02, 1.24);
      const fm = new THREE.MeshStandardMaterial({ color: 0x1f5a2e, roughness: 1 });
      const felt = new THREE.Mesh(fg, fm);
      felt.position.set(1.25, 0.82, 0.75);
      felt.receiveShadow = true;
      group.add(felt);
      disposables.push(fg, fm);
    }
    if (has('jukebox')) {
      // The lit arch on top: a half drum lying across the cabinet (its axis along x, the round side up); the
      // Meshy jukebox lights its own panels instead.
      if (!meshyJuke) jukeMat = glow(new THREE.CylinderGeometry(0.47, 0.47, 0.5, 20, 1, false, -Math.PI / 2, Math.PI).rotateZ(Math.PI / 2), 0xff9a3a, 1.2, [W - 0.45, 1.1, -1.55]);
      jukeLight = new THREE.PointLight(0xff8a3a, 3, 4, 1.8);
      jukeLight.position.set(W - 1.0, 1.0, -1.55);
      group.add(jukeLight);
      disposables.push(jukeLight);
    }
    if (has('bar')) {
      // The mirror behind the optics: a faint warm sheen.
      const mg = new THREE.PlaneGeometry(2.8, 0.7);
      const mm = new THREE.MeshStandardMaterial({ color: 0x8a8070, roughness: 0.15, metalness: 0.6, emissive: 0x2a2018, emissiveIntensity: 0.6 });
      const mirror = new THREE.Mesh(mg, mm);
      mirror.position.set(meshyBar ? BAR_AT.x : 2.25, 1.5, BACK + 0.05);
      group.add(mirror);
      disposables.push(mg, mm);
    }

    // Name cards over the figures.
    for (const l of labels) {
      const tex = nameTexture(l.name, l.you);
      if (!tex) continue;
      const m = new THREE.SpriteMaterial({ map: tex, depthTest: false, transparent: true });
      const s = new THREE.Sprite(m);
      s.scale.set(0.9, 0.225, 1);
      s.position.copy(l.pos);
      s.renderOrder = 10;
      group.add(s);
      disposables.push(m, tex);
    }

    this.scene.add(group);
    this.dressing = { group, disposables, jukeLight, jukeMat, jukeGlow };
    this.shown = [...owned];
    // The bare room is lit by one bulb; a furnished one glows warmer.
    this.bulb.intensity = owned.size ? 7 : 9;
  }

  private undress(): void {
    const d = this.dressing;
    if (!d) return;
    this.scene.remove(d.group);
    for (const x of d.disposables) x.dispose();
    this.dressing = null;
  }

  /** Frame the room into the part of the canvas the panel leaves free (CSS px). */
  setView(width: number, height: number, stage: Stage): void {
    const w = Math.max(1, width);
    const h = Math.max(1, height);
    const aspect = w / h;
    const fw = Math.max(0.25, stage.w / w);
    const fh = Math.max(0.25, stage.h / h);
    const dist = EYE.distanceTo(TARGET);
    const tanV = Math.max(FIT_HALF_H / dist / fh, FIT_HALF_W / dist / (fw * aspect));
    this.camera.aspect = aspect;
    this.camera.setViewOffset(w, h, w / 2 - (stage.x + stage.w / 2), h / 2 - (stage.y + stage.h / 2), w, h);
    this.camera.fov = THREE.MathUtils.clamp(THREE.MathUtils.radToDeg(2 * Math.atan(tanV)), 25, 95);
    this.camera.updateProjectionMatrix();
  }

  update(dt: number, playing: boolean): void {
    this.time += dt;
    // A slow drift, as if you were standing in the doorway taking it in.
    const t = this.time;
    this.camera.position.set(EYE.x + Math.sin(t * 0.13) * 0.18, EYE.y + Math.sin(t * 0.21) * 0.05, EYE.z);
    this.camera.lookAt(TARGET);
    // The bulb's small flicker; the jukebox's arch breathes when it plays.
    this.bulb.intensity += ((this.dressing?.group.children.length ? 7 : 9) * (1 + Math.sin(t * 17) * 0.015) - this.bulb.intensity) * 0.5;
    const d = this.dressing;
    if (d?.jukeMat) d.jukeMat.emissiveIntensity = playing ? 1.1 + Math.sin(t * 3.1) * 0.35 : 0.4;
    d?.jukeGlow?.(playing ? 1.6 + Math.sin(t * 3.1) * 0.5 : 0.5);
    if (d?.jukeLight) d.jukeLight.intensity = playing ? 3 + Math.sin(t * 3.1) * 0.8 : 0.8;
  }

  dispose(): void {
    this.disposed = true;
    this.undress();
    if (this.pack) releaseModelPack('hideout');
    this.pack = null;
    for (const d of this.disposables) d.dispose();
    this.disposables.length = 0;
    this.scene.clear();
  }
}
